import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../api/client';
import { useAuth } from '../state/auth';
import { Bandeau, Carte, Champ, Squelette } from '../components/ui';
import { centimes, montant } from '../lib/format';
import { T, reglagesRegionaux } from '../i18n';

interface Params {
  raisonSociale: string; rc: string; telephone: string; adresse: string; email: string; siteWeb: string;
  gerant: string; nif: string; nis: string; articleImposition: string; tauxTvaPct: number;
  deviseReference: string; tamponPreparationMin: number;
  ageMinimum: number; ancienneteMinimumPermis: number; rayonKm: number; wilayasAutorisees: string[];
  heureOuverture: string; heureFermeture: string;
  hauteSaisonDebut: string; hauteSaisonFin: string;
  conditionsGeneralesFr: { titre: string; texte: string }[];
  conditionsGeneralesAr: { titre: string; texte: string }[];
  mentionGeolocalisationFr: string; mentionGeolocalisationAr: string;
  pays?: string; locale?: string; fuseauHoraire?: string; uniteDistance?: 'km' | 'mi';
  nomTaxe?: string; taxeParDefaut?: boolean;
  identifiantsLegaux?: { libelle: string; valeur: string }[];
  langueDocuments?: 'fr' | 'en'; langueDocumentsSecondaire?: '' | 'ar' | 'fr' | 'en';
}

/* Codes, pas du texte : les libellés viennent de T.parametres. */
const PAYS = ['DZ', 'FR', 'BE', 'CH', 'CA', 'GB', 'IE', 'US', 'AU', 'NZ', 'MA', 'TN', 'ES', 'PT'];
const LOCALES = ['fr-DZ', 'fr-FR', 'fr-BE', 'fr-CH', 'fr-CA', 'fr-MA', 'fr-TN', 'en-GB', 'en-IE', 'en-US', 'en-AU', 'en-CA', 'en-NZ'];
/* Libellés officiels des identifiants : noms propres administratifs, non traduits. */
const IDENTIFIANTS_PAYS: Record<string, string[]> = {
  DZ: ['RC', 'NIF', 'NIS', "Article d'imposition"],
  FR: ['SIRET', 'N° TVA intracommunautaire'],
  BE: ["N° d'entreprise (BCE)", 'N° TVA'],
  CH: ['IDE/UID'],
  CA: ["NE (Numéro d'entreprise / Business Number)", 'TPS/GST', 'TVQ/QST'],
  GB: ['Company number', 'VAT number'],
  IE: ['CRO number', 'VAT number'],
  US: ['EIN', 'State tax ID'],
  AU: ['ABN'],
  NZ: ['NZBN', 'GST number'],
  MA: ['RC', 'ICE', 'IF'],
  TN: ['Matricule fiscal', 'RNE'],
  ES: ['CIF/NIF'],
  PT: ['NIF/NIPC'],
};
const fuseaux = (): string[] => {
  try { return (Intl as unknown as { supportedValuesOf(k: string): string[] }).supportedValuesOf('timeZone'); }
  catch { return ['Africa/Algiers', 'Europe/Paris', 'Europe/London', 'America/New_York', 'Australia/Sydney']; }
};
interface Supp { _id: string; code: string; libelle: string; libelleAr: string; unite: string; prixMinor: number; moment: string; automatique: boolean; articleContrat?: string; actif: boolean }

export default function Parametres() {
  const { peut } = useAuth();
  const modifiable = peut('gerer.parametres');
  const [p, setP] = useState<Params | null>(null);
  const [supp, setSupp] = useState<Supp[]>([]);
  /*
   * L'onglet se lit dans l'adresse : /parametres?onglet=supplements. Les modes
   * d'emploi disent « Paramètres › Suppléments » — autant que ce soit un lien
   * qui ouvre directement le bon onglet. Valeur absente ou inconnue : agence.
   */
  const [onglet, setOnglet] = useState<'agence' | 'regles' | 'supplements' | 'cgl'>(() => {
    const o = new URLSearchParams(window.location.search).get('onglet');
    return o === 'regles' || o === 'supplements' || o === 'cgl' ? o : 'agence';
  });
  const [msg, setMsg] = useState('');
  const [erreur, setErreur] = useState('');

  const charger = useCallback(() => {
    api.get<Params>('/parametres').then(setP);
    api.get<Supp[]>('/supplements').then(setSupp);
  }, []);
  useEffect(charger, [charger]);
  if (!p) return <Squelette lignes={8} />;

  async function sauver(patch: Partial<Params>) {
    setErreur(''); setMsg('');
    try { setP(await api.patch<Params>('/parametres', patch)); setMsg(T.parametres.enregistre); }
    catch (e) { setErreur(e instanceof ApiError ? e.erreur : T.parametres.erreur); }
  }
  async function sauverSupp(s: Supp, prix: string) {
    setErreur('');
    try { await api.patch(`/supplements/${s._id}`, { prixMinor: centimes(Number(prix || 0)) }); setMsg(T.parametres.suppMisAJour(s.libelle)); charger(); }
    catch (e) { setErreur(e instanceof ApiError ? e.erreur : T.parametres.erreur); }
  }

  async function sauverIntl() {
    if (!p) return;
    setErreur(''); setMsg('');
    try {
      setP(await api.patch<Params>('/parametres', {
        pays: p.pays, locale: p.locale, fuseauHoraire: p.fuseauHoraire, uniteDistance: p.uniteDistance,
        deviseReference: p.deviseReference, tauxTvaPct: p.tauxTvaPct, nomTaxe: p.nomTaxe,
        taxeParDefaut: p.taxeParDefaut, langueDocuments: p.langueDocuments,
        langueDocumentsSecondaire: p.langueDocumentsSecondaire,
        identifiantsLegaux: (p.identifiantsLegaux ?? []).filter((x) => x.libelle.trim() || x.valeur.trim()),
      }));
      setMsg(T.parametres.enregistreRecharger);
    } catch (e) { setErreur(e instanceof ApiError ? e.erreur : T.parametres.erreur); }
  }

  const tp = T.parametres;
  const pays = p.pays ?? 'DZ';
  const ld = p.langueDocuments ?? 'fr';
  const ld2 = p.langueDocumentsSecondaire ?? 'ar';
  /* Agence algérienne en fr/ar : on garde mot pour mot les libellés d'origine. */
  const libellesHistoriques = ld === 'fr' && ld2 === 'ar';
  const ids = p.identifiantsLegaux ?? [];
  const setIds = (v: { libelle: string; valeur: string }[]) => setP({ ...p, identifiantsLegaux: v.slice(0, 8) });
  const libelleRegions = (p.locale ?? 'fr-DZ').startsWith('fr') && pays === 'DZ' ? tp.wilayas : tp.regions;
  const unite = reglagesRegionaux().uniteDistance;
  const listeFuseaux = fuseaux();

  const arManquant = ld2 !== 'ar' ? 0 : p.conditionsGeneralesAr.filter((a) => a.texte.startsWith('[')).length;
  const suppSansPrix = supp.filter((s) => s.prixMinor === 0 && s.code !== 'dommage');

  return (
    <>
      <div className="entete">
        <div className="titres"><h1>{tp.titre}</h1><p>{modifiable ? tp.reserve : tp.lectureSeule}</p></div>
        <div className="actions">
          {(['agence', 'regles', 'supplements', 'cgl'] as const).map((o) => (
            <button key={o} className={`btn btn-s ${onglet === o ? 'btn-p' : ''}`} onClick={() => setOnglet(o)}>
              {tp.onglets[o]}
            </button>
          ))}
        </div>
      </div>

      {msg && <div style={{ marginBottom: 14 }}><Bandeau type="ok">{msg}</Bandeau></div>}
      {erreur && <div style={{ marginBottom: 14 }}><Bandeau type="ko">{erreur}</Bandeau></div>}
      {onglet !== 'cgl' && arManquant > 0 && (
        <div style={{ marginBottom: 14 }}>
          <Bandeau type="warn" titre={tp.arManquantTitre(arManquant)}>
            {tp.arManquantTexte} <button className="btn btn-s" style={{ marginLeft: 8 }} onClick={() => setOnglet('cgl')}>{tp.lesCompleter}</button>
          </Bandeau>
        </div>
      )}

      {onglet === 'agence' && (
        <div style={{ marginBottom: 14 }}>
        <Carte titre={tp.intl.titre}>
          <div className="grille g2">
            <Champ label={tp.intl.pays} aide={tp.intl.paysAide}>
              <select value={pays} disabled={!modifiable} onChange={(e) => setP({ ...p, pays: e.target.value })}>
                {(PAYS.includes(pays) ? PAYS : [pays, ...PAYS]).map((c) => <option key={c} value={c}>{tp.pays[c] ?? c}</option>)}
              </select>
            </Champ>
            <Champ label={tp.intl.locale} aide={tp.intl.localeAide}>
              <select value={p.locale ?? 'fr-DZ'} disabled={!modifiable} onChange={(e) => setP({ ...p, locale: e.target.value })}>
                {(LOCALES.includes(p.locale ?? 'fr-DZ') ? LOCALES : [p.locale ?? '', ...LOCALES]).map((c) => <option key={c} value={c}>{tp.locales[c] ?? c}</option>)}
              </select>
            </Champ>
            <Champ label={tp.intl.fuseau} aide={tp.intl.fuseauAide}>
              <input list="liste-fuseaux" value={p.fuseauHoraire ?? ''} readOnly={!modifiable} placeholder={tp.intl.fuseauFiltre}
                     onChange={(e) => setP({ ...p, fuseauHoraire: e.target.value })} />
              <datalist id="liste-fuseaux">{listeFuseaux.map((z) => <option key={z} value={z} />)}</datalist>
            </Champ>
            <Champ label={tp.intl.devise} aide={tp.intl.deviseAide}>
              <input value={p.deviseReference ?? ''} maxLength={3} readOnly={!modifiable} style={{ maxWidth: 160, textTransform: 'uppercase' }}
                     onChange={(e) => setP({ ...p, deviseReference: e.target.value.toUpperCase() })} />
            </Champ>
            <Champ label={tp.intl.unite}>
              <select value={p.uniteDistance ?? 'km'} disabled={!modifiable} onChange={(e) => setP({ ...p, uniteDistance: e.target.value as 'km' | 'mi' })}>
                <option value="km">{tp.intl.uniteKm}</option>
                <option value="mi">{tp.intl.uniteMi}</option>
              </select>
            </Champ>
            <Champ label={tp.intl.nomTaxe} aide={tp.intl.nomTaxeAide}>
              <input value={p.nomTaxe ?? ''} maxLength={20} readOnly={!modifiable} style={{ maxWidth: 200 }}
                     onChange={(e) => setP({ ...p, nomTaxe: e.target.value })} />
            </Champ>
            <Champ label={tp.intl.tauxTaxe} aide={tp.tauxTvaAide}>
              <input type="number" min={0} max={100} step={0.1} style={{ maxWidth: 160 }}
                     value={p.tauxTvaPct ?? 19} readOnly={!modifiable}
                     onChange={(e) => setP({ ...p, tauxTvaPct: Number(e.target.value) })} />
            </Champ>
            <Champ label={tp.intl.taxeParDefaut}>
              <input type="checkbox" style={{ width: 20, height: 20 }} checked={!!p.taxeParDefaut} disabled={!modifiable}
                     onChange={(e) => setP({ ...p, taxeParDefaut: e.target.checked })} />
            </Champ>
            <Champ label={tp.intl.langueDocs} aide={tp.intl.langueDocsAide}>
              <select value={ld} disabled={!modifiable} onChange={(e) => setP({ ...p, langueDocuments: e.target.value as 'fr' | 'en' })}>
                {(['fr', 'en'] as const).map((l) => <option key={l} value={l}>{tp.intl.langues[l]}</option>)}
              </select>
            </Champ>
            <Champ label={tp.intl.langueDocs2} aide={tp.intl.langueDocs2Aide}>
              <select value={ld2} disabled={!modifiable} onChange={(e) => setP({ ...p, langueDocumentsSecondaire: e.target.value as '' | 'ar' | 'fr' | 'en' })}>
                <option value="">{tp.intl.aucune}</option>
                {(['ar', 'fr', 'en'] as const).filter((l) => l !== ld).map((l) => <option key={l} value={l}>{tp.intl.langues[l]}</option>)}
              </select>
            </Champ>
          </div>

          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--bord)' }}>
            <Champ label={tp.intl.identifiants} aide={tp.intl.identifiantsAide}>
              <div style={{ display: 'grid', gap: 8 }}>
                {ids.map((x, i) => (
                  <div key={i} style={{ display: 'flex', gap: 8 }}>
                    <input aria-label={tp.intl.libelle} placeholder={tp.intl.libelle} value={x.libelle} readOnly={!modifiable} style={{ flex: 1 }}
                           onChange={(e) => { const c = [...ids]; c[i] = { ...x, libelle: e.target.value }; setIds(c); }} />
                    <input aria-label={tp.intl.valeur} placeholder={tp.intl.valeur} value={x.valeur} readOnly={!modifiable} style={{ flex: 1 }}
                           onChange={(e) => { const c = [...ids]; c[i] = { ...x, valeur: e.target.value }; setIds(c); }} />
                    {modifiable && <button className="btn btn-s" onClick={() => setIds(ids.filter((_, j) => j !== i))}>{tp.intl.retirer}</button>}
                  </div>
                ))}
              </div>
            </Champ>
            {modifiable && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                {ids.length < 8 && <button className="btn btn-s" onClick={() => setIds([...ids, { libelle: '', valeur: '' }])}>{tp.intl.ajouterIdentifiant}</button>}
                {IDENTIFIANTS_PAYS[pays] && (
                  <button className="btn btn-s" onClick={() => {
                    if (ids.some((x) => x.valeur.trim()) && !confirm(tp.intl.remplacerIdentifiants)) return;
                    setIds((IDENTIFIANTS_PAYS[pays] ?? []).map((libelle) => ({ libelle, valeur: ids.find((x) => x.libelle === libelle)?.valeur ?? '' })));
                  }}>{tp.intl.preremplir(tp.pays[pays] ?? pays)}</button>
                )}
              </div>
            )}
          </div>
          <p className="mini sourdine" style={{ marginTop: 12 }}>{tp.intl.rechargement}</p>
          {modifiable && <button className="btn btn-p" style={{ marginTop: 6 }} onClick={() => void sauverIntl()}>{tp.intl.enregistrer}</button>}
        </Carte>
        </div>
      )}

      {onglet === 'agence' && (
        <Carte titre={tp.identite}>
          <div className="grille g2">
            {(['raisonSociale', 'gerant', 'rc', 'nif', 'nis', 'articleImposition', 'telephone', 'email', 'adresse', 'siteWeb'] as const).map((k) => (
              <Champ key={k} label={tp.champs[k]}>
                <input value={p[k] ?? ''} readOnly={!modifiable} onChange={(e) => setP({ ...p, [k]: e.target.value })} />
              </Champ>
            ))}
          </div>

          {/* Ces cinq lignes sont celles que porte une facture algerienne.
            * Sans elles, la facture n'est pas opposable a une societe. */}
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--bord)' }}>
            <Champ label={tp.tauxTva}
                   aide={tp.tauxTvaAide}>
              <input type="number" min={0} max={100} step={1} style={{ maxWidth: 160 }}
                     value={p.tauxTvaPct ?? 19} readOnly={!modifiable}
                     onChange={(e) => setP({ ...p, tauxTvaPct: Number(e.target.value) })} />
            </Champ>
          </div>
          {modifiable && (
            <button className="btn btn-p" style={{ marginTop: 14 }} onClick={() => void sauver({
              raisonSociale: p.raisonSociale, rc: p.rc, telephone: p.telephone, email: p.email,
              adresse: p.adresse, siteWeb: p.siteWeb,
              gerant: p.gerant, nif: p.nif, nis: p.nis,
              articleImposition: p.articleImposition, tauxTvaPct: p.tauxTvaPct,
            })}>{T.commun.actions.enregistrer}</button>
          )}
        </Carte>
      )}

      {onglet === 'regles' && (
        <div className="grille g2" style={{ alignItems: 'start' }}>
          <Carte titre={tp.article01}>
            <div className="grille g2">
              <Champ label={tp.ageMinimum} aide={tp.ageMinimumAide}>
                <input type="number" value={p.ageMinimum} readOnly={!modifiable} onChange={(e) => setP({ ...p, ageMinimum: Number(e.target.value) })} />
              </Champ>
              <Champ label={tp.anciennetePermis}>
                <input type="number" value={p.ancienneteMinimumPermis} readOnly={!modifiable} onChange={(e) => setP({ ...p, ancienneteMinimumPermis: Number(e.target.value) })} />
              </Champ>
              <Champ label={tp.rayon(unite)}>
                <input type="number" value={p.rayonKm} readOnly={!modifiable} onChange={(e) => setP({ ...p, rayonKm: Number(e.target.value) })} />
              </Champ>
              <Champ label={libelleRegions}>
                <input value={p.wilayasAutorisees.join(', ')} readOnly={!modifiable}
                       onChange={(e) => setP({ ...p, wilayasAutorisees: e.target.value.split(',').map((x) => x.trim()) })} />
              </Champ>
            </div>
          </Carte>

          <Carte titre={tp.article02}>
            <div className="grille g2">
              <Champ label={tp.ouverture}><input type="time" value={p.heureOuverture} readOnly={!modifiable} onChange={(e) => setP({ ...p, heureOuverture: e.target.value })} /></Champ>
              <Champ label={tp.fermeture}><input type="time" value={p.heureFermeture} readOnly={!modifiable} onChange={(e) => setP({ ...p, heureFermeture: e.target.value })} /></Champ>
              <Champ label={tp.hauteDebut} aide={tp.hauteDebutAide}>
                <input value={p.hauteSaisonDebut} readOnly={!modifiable} onChange={(e) => setP({ ...p, hauteSaisonDebut: e.target.value })} placeholder="06-01" />
              </Champ>
              <Champ label={tp.hauteFin}>
                <input value={p.hauteSaisonFin} readOnly={!modifiable} onChange={(e) => setP({ ...p, hauteSaisonFin: e.target.value })} placeholder="09-30" />
              </Champ>
              <Champ label={tp.preparation} aide={tp.preparationAide}>
                <input type="number" value={p.tamponPreparationMin} readOnly={!modifiable} onChange={(e) => setP({ ...p, tamponPreparationMin: Number(e.target.value) })} />
              </Champ>
            </div>
          </Carte>

          {modifiable && (
            <button className="btn btn-p btn-l" onClick={() => void sauver({
              ageMinimum: p.ageMinimum, ancienneteMinimumPermis: p.ancienneteMinimumPermis,
              rayonKm: p.rayonKm, wilayasAutorisees: p.wilayasAutorisees,
              heureOuverture: p.heureOuverture, heureFermeture: p.heureFermeture,
              hauteSaisonDebut: p.hauteSaisonDebut, hauteSaisonFin: p.hauteSaisonFin,
              tamponPreparationMin: p.tamponPreparationMin,
            })}>{tp.enregistrerRegles}</button>
          )}
        </div>
      )}

      {onglet === 'supplements' && (
        <>
          {suppSansPrix.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <Bandeau type="warn" titre={tp.suppSansPrixTitre(suppSansPrix.length)}>
                {suppSansPrix.map((s) => s.libelle).join(', ')} {tp.suppSansPrixTexte}
              </Bandeau>
            </div>
          )}
          <Carte titre={tp.catalogue} pad={false}>
            <div className="enveloppe-tbl">
              <table className="tbl">
                <thead><tr><th>{tp.colonnes.supplement}</th><th>{libellesHistoriques ? tp.colonnes.arabe : (ld2 ? (tp.intl.langues[ld2] ?? ld2) : '')}</th><th>{tp.colonnes.unite}</th><th>{tp.colonnes.moment}</th><th>{tp.colonnes.article}</th><th className="droite">{tp.colonnes.prix}</th><th /></tr></thead>
                <tbody>
                  {supp.map((s) => <LigneSupp key={s._id} s={s} modifiable={peut('gerer.supplements')} onSauver={sauverSupp} />)}
                </tbody>
              </table>
            </div>
          </Carte>
          <p className="mini sourdine" style={{ marginTop: 12 }}>
            {tp.suppNote}
          </p>
        </>
      )}

      {onglet === 'cgl' && (
        <>
          <div style={{ marginBottom: 14 }}>
            <Bandeau type="info" titre={tp.cglTitre}>
              {libellesHistoriques
                ? <>{tp.cglTexte1}<b>{tp.cglTexte2}</b>{tp.cglTexte3}</>
                : tp.cglTexteIntl(tp.intl.langues[ld] ?? ld, ld2 ? (tp.intl.langues[ld2] ?? ld2) : '')}
            </Bandeau>
          </div>
          <div style={{ display: 'grid', gap: 14 }}>
            {p.conditionsGeneralesFr.map((a, i) => (
              <Carte key={i} titre={a.titre}>
                <div className="grille g2">
                  <Champ label={libellesHistoriques ? tp.francais : tp.languePrincipale(tp.intl.langues[ld] ?? ld)}>
                    <textarea style={{ minHeight: 130 }} value={a.texte} readOnly={!modifiable}
                              onChange={(e) => { const c = [...p.conditionsGeneralesFr]; c[i] = { ...a, texte: e.target.value }; setP({ ...p, conditionsGeneralesFr: c }); }} />
                  </Champ>
                  <Champ label={libellesHistoriques ? (p.conditionsGeneralesAr[i]?.titre ?? tp.arabe) : tp.langueSeconde(ld2 ? (tp.intl.langues[ld2] ?? ld2) : tp.intl.aucune)}
                         erreur={ld2 === 'ar' && p.conditionsGeneralesAr[i]?.texte.startsWith('[') ? tp.arabeManquant : undefined}>
                    <textarea dir={ld2 === 'ar' ? 'rtl' : 'ltr'} style={{ minHeight: 130, fontFamily: '"Noto Sans Arabic",Tahoma,sans-serif' }}
                              value={p.conditionsGeneralesAr[i]?.texte ?? ''} readOnly={!modifiable}
                              onChange={(e) => { const c = [...p.conditionsGeneralesAr]; c[i] = { titre: c[i]?.titre ?? '', texte: e.target.value }; setP({ ...p, conditionsGeneralesAr: c }); }} />
                  </Champ>
                </div>
              </Carte>
            ))}
            <Carte titre={tp.geoloc}>
              <div className="grille g2">
                <Champ label={libellesHistoriques ? tp.francais : tp.languePrincipale(tp.intl.langues[ld] ?? ld)}>
                  <textarea value={p.mentionGeolocalisationFr} readOnly={!modifiable} onChange={(e) => setP({ ...p, mentionGeolocalisationFr: e.target.value })} />
                </Champ>
                <Champ label={libellesHistoriques ? tp.arabe : tp.langueSeconde(ld2 ? (tp.intl.langues[ld2] ?? ld2) : tp.intl.aucune)} erreur={ld2 === 'ar' && p.mentionGeolocalisationAr.startsWith('[') ? tp.aColler : undefined}>
                  <textarea dir={ld2 === 'ar' ? 'rtl' : 'ltr'} style={{ fontFamily: '"Noto Sans Arabic",Tahoma,sans-serif' }}
                            value={p.mentionGeolocalisationAr} readOnly={!modifiable} onChange={(e) => setP({ ...p, mentionGeolocalisationAr: e.target.value })} />
                </Champ>
              </div>
            </Carte>
            {modifiable && (
              <button className="btn btn-p btn-l" onClick={() => void sauver({
                conditionsGeneralesFr: p.conditionsGeneralesFr, conditionsGeneralesAr: p.conditionsGeneralesAr,
                mentionGeolocalisationFr: p.mentionGeolocalisationFr, mentionGeolocalisationAr: p.mentionGeolocalisationAr,
              })}>{tp.enregistrerCgl}</button>
            )}
          </div>
        </>
      )}
    </>
  );
}

/* 'depart' / 'retour' sont les cles du catalogue — jamais du texte a lire. */

function LigneSupp({ s, modifiable, onSauver }: { s: Supp; modifiable: boolean; onSauver: (s: Supp, prix: string) => void }) {
  const [prix, setPrix] = useState(String(s.prixMinor / 100));
  const change = prix !== String(s.prixMinor / 100);
  return (
    <tr>
      <td><b>{s.libelle}</b> {s.automatique && <span className="badge en_cours" style={{ marginLeft: 4 }}>{T.parametres.auto}</span>}</td>
      <td dir="rtl" style={{ fontFamily: '"Noto Sans Arabic",Tahoma,sans-serif' }}>{s.libelleAr || '—'}</td>
      <td className="mini sourdine">{T.parametres.parUnite(s.unite)}</td>
      <td className="mini sourdine">{T.parametres.moment[s.moment] ?? s.moment}</td>
      <td className="mini sourdine">{s.articleContrat ? T.parametres.art(s.articleContrat) : '—'}</td>
      <td className="droite" style={{ width: 170 }}>
        {modifiable
          ? <input type="number" value={prix} onChange={(e) => setPrix(e.target.value)}
                   style={{ width: 120, textAlign: 'right', height: 32, border: '1px solid var(--bord-fort)', borderRadius: 8, padding: '0 8px' }} />
          : <span className="montant">{montant(s.prixMinor)}</span>}
      </td>
      <td className="droite">{modifiable && change && <button className="btn btn-s btn-p" onClick={() => onSauver(s, prix)}>{T.commun.actions.enregistrer}</button>}</td>
    </tr>
  );
}
